/**
 * useFiasAI — Make a Request's engine adapter over the GLOBAL Fias AI
 * assistant (Phase 3b cutover).
 *
 * Make a Request and the global assistant window are ONE engine and ONE
 * thread store: AI turns go through the orchestrator (`assistantTurn` via
 * useGlobalFiasAI) — multi-step tools, confirmations, clarifying
 * questions, arche search as a model tool. This hook adapts the global
 * engine to the page's existing surface (mode toggles, FiasAIMessage
 * shape, sidebar search-result sync) so the page keeps its structure.
 *
 * Search-only mode stays a page-local flow (direct marketplace search, no
 * AI turn) appended into the same thread; the page persists those
 * exchanges client-side exactly as before. AI-turn transcripts are
 * persisted SERVER-side by the orchestrator — the page must NOT re-save
 * them (see MakeARequestPage's pendingSaveRef gating).
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  GUEST_TRIAL_ENDED_CTA,
  GUEST_TRIAL_ENDED_SIGNUP_PATH,
  GUEST_TRIAL_ENDED_TITLE,
  useIsGuestAccount,
} from '@/credits/guest-trial-cta';
import { api } from '@/lib/api';
import { useGlobalFiasAI } from '@/components/fias-ai';
import type { AgentChatMessage, AgentEvent, ArcheSuggestion } from '@/components/fias-ai';

export interface FiasAIMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  /** Agent activity (tool feed / confirmation / question cards). */
  events?: AgentEvent[];
  searchResults?: Array<{
    archId: string;
    name: string;
    simpleDescription: string;
    imageRef: string | null;
    fitnessScore: number;
    relevance?: number;
  }>;
  recommendedArches?: Array<{
    archId: string;
    name: string;
    simpleDescription: string;
    imageRef: string | null;
  }>;
  modelInfo?: {
    provider: string;
    modelName: string;
    displayName: string;
    tier: string;
  } | null;
}

interface UseFiasAIReturn {
  mode: { searchEnabled: boolean; aiEnabled: boolean };
  setSearchEnabled: (v: boolean) => void;
  setAIEnabled: (v: boolean) => void;
  messages: FiasAIMessage[];
  /** Replace-only (restore / clear). AI turns flow through submit. */
  setMessages: (messages: FiasAIMessage[]) => void;
  /** Hydrate a conversation from the sidebar so the next turn continues it. */
  hydrateConversation: (conversationId: string, messages: FiasAIMessage[]) => void;
  /** Bind a conversation created by the search-only save path to the engine. */
  adoptConversation: (conversationId: string) => void;
  submit: (
    query: string,
    options?: { aiOutOfCredits?: boolean; searchOutOfCredits?: boolean },
  ) => Promise<void>;
  stop: () => void;
  reset: () => void;
  isStreaming: boolean;
  isSearching: boolean;
  busyState: 'idle' | 'thinking' | 'awaiting_confirmation' | 'awaiting_user';
  pendingQuestionId: string | null;
  pendingConfirmationId: string | null;
  respondToConfirmation: (interactionId: string, approved: boolean, feedback?: string) => void;
  currentModel: {
    provider: string;
    modelName: string;
    displayName: string;
    tier: string;
  } | null;
  /** Latest search results for sidebar sync */
  latestSearchResults: FiasAIMessage['searchResults'];
  latestSearchQuery: string | null;
}

export interface UseFiasAIOptions {
  initialSearchEnabled?: boolean;
  initialAIEnabled?: boolean;
}

/** Suggestions for one assistant message: restored metadata or live arche refs. */
function suggestionsFor(msg: AgentChatMessage): ArcheSuggestion[] | undefined {
  if (msg.archeSuggestions && msg.archeSuggestions.length > 0) return msg.archeSuggestions;
  const refs = (msg.events ?? [])
    .filter((e): e is Extract<AgentEvent, { kind: 'tool' }> => e.kind === 'tool')
    .flatMap((e) => e.refs ?? [])
    .filter((r) => r.type === 'arche');
  if (refs.length === 0) return undefined;
  return refs.map((r) => ({
    archId: r.id,
    name: r.label,
    simpleDescription: (r as { description?: string }).description ?? '',
    imageRef: (r as { imageUrl?: string | null }).imageUrl ?? null,
  }));
}

export function useFiasAI(options?: UseFiasAIOptions): UseFiasAIReturn {
  const global = useGlobalFiasAI();
  const isGuest = useIsGuestAccount();
  const [searchEnabled, setSearchEnabled] = useState(options?.initialSearchEnabled ?? true);
  const [aiEnabled, setAIEnabled] = useState(options?.initialAIEnabled ?? true);
  const [isSearching, setIsSearching] = useState(false);
  const [latestSearchResults, setLatestSearchResults] =
    useState<FiasAIMessage['searchResults']>(undefined);
  const [latestSearchQuery, setLatestSearchQuery] = useState<string | null>(null);

  // ---- View mapping: AgentChatMessage → FiasAIMessage ----------------------
  const messages = useMemo<FiasAIMessage[]>(
    () =>
      global.messages.map((msg) => {
        const suggestions = msg.role === 'assistant' ? suggestionsFor(msg) : undefined;
        return {
          id: msg.id,
          role: msg.role,
          content: msg.content,
          isStreaming: msg.isStreaming,
          events: msg.events,
          modelInfo: msg.modelInfo,
          searchResults: (msg as FiasAIMessage).searchResults,
          recommendedArches: suggestions,
        };
      }),
    [global.messages],
  );

  // Sync the sidebar with the newest arche suggestions from live AI turns.
  const lastSyncedRef = useRef<string | null>(null);
  useEffect(() => {
    for (let i = global.messages.length - 1; i >= 0; i--) {
      const msg = global.messages[i];
      if (msg.role !== 'assistant') continue;
      const suggestions = suggestionsFor(msg);
      if (suggestions && suggestions.length > 0) {
        const key = `${msg.id}:${suggestions.length}`;
        if (lastSyncedRef.current !== key) {
          lastSyncedRef.current = key;
          setLatestSearchResults(
            suggestions.map((sug) => ({
              archId: sug.archId,
              name: sug.name,
              simpleDescription: sug.simpleDescription,
              imageRef: sug.imageRef,
              fitnessScore: 0,
            })),
          );
        }
        return;
      }
    }
  }, [global.messages]);

  const setMessages = useCallback(
    (msgs: FiasAIMessage[]) => {
      global.replaceMessages(
        msgs as AgentChatMessage[],
        msgs.length > 0 ? global.conversationId : null,
      );
    },
    [global],
  );

  const hydrateConversation = useCallback(
    (conversationId: string, msgs: FiasAIMessage[]) => {
      global.replaceMessages(
        msgs.map((m) => ({
          ...m,
          archeSuggestions: m.recommendedArches ?? undefined,
        })) as AgentChatMessage[],
        conversationId,
      );
    },
    [global],
  );

  const submit = useCallback(
    async (query: string, opts?: { aiOutOfCredits?: boolean; searchOutOfCredits?: boolean }) => {
      const trimmedQuery = query.trim();
      if (!trimmedQuery || global.isStreaming || isSearching) return;

      const useAI = aiEnabled && !opts?.aiOutOfCredits;

      if (!useAI) {
        // ---- Search-only: direct marketplace search, appended locally ----
        const userMessageId = `user-${Date.now()}`;
        const assistantMessageId = `assistant-${Date.now()}`;
        global.appendMessages([{ id: userMessageId, role: 'user', content: trimmedQuery }]);
        setIsSearching(true);
        try {
          const result = await api.searchMarketplace({
            q: trimmedQuery,
            limit: 10,
            enableSemanticSearch: !opts?.searchOutOfCredits,
          });
          const mapped = result.arches
            .filter((a) => a.archId !== 'arche_make_a_request')
            .map((a) => ({
              archId: a.archId,
              name: a.name,
              simpleDescription: a.simpleDescription,
              imageRef: a.imageRef,
              fitnessScore: a.fitnessScore,
              relevance: a.relevance,
            }));

          setLatestSearchResults(mapped.length > 0 ? mapped : undefined);
          setLatestSearchQuery(trimmedQuery);

          // A guest cannot buy credits (its trial is a one-time grant), so it
          // is sent to sign up instead of the store.
          const outOfCreditsNote = !opts?.searchOutOfCredits
            ? ''
            : isGuest
              ? `\n\nAI-powered semantic search is disabled — ${GUEST_TRIAL_ENDED_TITLE.toLowerCase()}. [${GUEST_TRIAL_ENDED_CTA}](${GUEST_TRIAL_ENDED_SIGNUP_PATH}).`
              : '\n\nAI-powered semantic search is disabled — your credit balance is too low. [Buy more credits](/credits) to re-enable semantic Arche search.';
          const baseContent =
            mapped.length > 0
              ? 'Here are the top matches for your Re-Quest:'
              : 'No arches found matching your request. Try a different search or browse the Arche Store.';

          global.appendMessages([
            {
              id: assistantMessageId,
              role: 'assistant',
              content: baseContent + outOfCreditsNote,
              searchResults: mapped.length > 0 ? mapped : undefined,
            } as AgentChatMessage,
          ]);
        } catch (err) {
          console.error('Search failed:', err);
          global.appendMessages([
            {
              id: assistantMessageId,
              role: 'assistant',
              content: 'Search failed. Please try again.',
            },
          ]);
        } finally {
          setIsSearching(false);
        }
        return;
      }

      // ---- AI turn: the orchestrator (server-persisted transcript) --------
      setLatestSearchQuery(trimmedQuery);
      await global.submit(trimmedQuery, {
        origin: 'make_a_request',
        // The "Arche Search" toggle keeps meaning in AI mode: off = the
        // model must not search the store this turn.
        archeSearchEnabled: searchEnabled,
      });
    },
    [aiEnabled, searchEnabled, isSearching, global, isGuest],
  );

  const reset = useCallback(() => {
    global.reset();
    setIsSearching(false);
    setLatestSearchResults(undefined);
    setLatestSearchQuery(null);
    lastSyncedRef.current = null;
  }, [global]);

  const respondToConfirmation = useCallback(
    (interactionId: string, approved: boolean, feedback?: string) => {
      if (global.pendingConfirmation?.interactionId === interactionId) {
        void global.respondToConfirmation(approved, feedback);
      }
    },
    [global],
  );

  return {
    mode: { searchEnabled, aiEnabled },
    setSearchEnabled,
    setAIEnabled,
    messages,
    setMessages,
    hydrateConversation,
    adoptConversation: global.adoptConversation,
    submit,
    stop: global.stop,
    reset,
    isStreaming: global.isStreaming,
    isSearching,
    busyState: global.busyState,
    pendingQuestionId: global.pendingQuestion?.interactionId ?? null,
    pendingConfirmationId: global.pendingConfirmation?.interactionId ?? null,
    respondToConfirmation,
    currentModel: global.currentModel,
    latestSearchResults,
    latestSearchQuery,
  };
}
